import { EventEmitter } from '../utils/event-emitter.js';
import { querySelector, throttle, getScrollPosition } from '../utils/helpers.js';
import {
  SELECTORS,
  CSS_CLASSES,
  EVENTS,
  NAVIGATION_CONFIG,
} from '../utils/constants.js';

/**
 * Менеджер навигации
 * Отвечает за управление навигацией, sticky меню и плавную прокрутку
 */
export class NavigationManager extends EventEmitter {
  constructor() {
    super();
    this.navigationBar = null;
    this.stickyLogo = null;
    this.header = null;

    this.navbarOffsetTop = 0;
    this.headerHeight = 0;
    this.isSticky = false;

    this.throttledScrollHandler = null;
    this.eventsInitialized = false;
  }

  /**
   * Инициализация менеджера
   */
  init() {
    this.navigationBar = querySelector(SELECTORS.NAVIGATION_BAR);
    this.stickyLogo = querySelector(SELECTORS.STICKY_LOGO);
    this.header = querySelector('header');

    if (!this.navigationBar) {
      throw new Error('Навигационная панель не найдена');
    }

    this.calculateInitialPositions();
    this.setupEventListeners();
    this.setupAccessibility();

    console.log('🧭 NavigationManager инициализирован');
  }

  /**
   * Вычисление начальных позиций элементов
   */
  calculateInitialPositions() {
    // Получаем начальное положение навигации
    this.navbarOffsetTop = this.navigationBar.offsetTop;

    // Получаем высоту header
    if (this.header) {
      this.headerHeight = this.header.offsetHeight;
    }

    console.log(`📏 Позиции: navbar offset ${this.navbarOffsetTop}px, header height ${this.headerHeight}px`);
  }

  /**
   * Настройка обработчиков событий
   */
  setupEventListeners() {
    if (this.eventsInitialized) return;

    // Throttled обработчик скролла
    this.throttledScrollHandler = throttle(
      this.handleScroll.bind(this),
      10, // Более частое обновление для плавности sticky эффекта
    );

    // Обработчик скролла
    window.addEventListener('scroll', this.throttledScrollHandler, { passive: true });

    // Обработчик изменения размера окна
    window.addEventListener('resize', throttle(() => {
      this.handleResize();
    }, 250));

    // Обработчики кликов по навигационным элементам
    this.setupNavigationClicks();

    this.eventsInitialized = true;
  }

  /**
   * Настройка accessibility для навигации
   */
  setupAccessibility() {
    if (this.navigationBar) {
      this.navigationBar.setAttribute('role', 'navigation');
      this.navigationBar.setAttribute('aria-label', 'Основная навигация');
    }

    // Добавляем ARIA атрибуты к ссылкам навигации
    const navItems = this.navigationBar?.querySelectorAll('.nav-item');
    navItems?.forEach((item, index) => {
      item.setAttribute('role', 'menuitem');
      item.setAttribute('tabindex', index === 0 ? '0' : '-1');
    });
  }

  /**
   * Настройка обработчиков кликов по навигации
   */
  setupNavigationClicks() {
    const navItems = this.navigationBar?.querySelectorAll('.nav-item');
    navItems?.forEach(item => {
      item.addEventListener('click', (event) => {
        this.handleNavigationClick(event, item);
      });
    });
    
    // Отдельный обработчик для ссылки "Прайс"
    const priceLink = querySelector(SELECTORS.PRICE_LINK);
    if (priceLink) {
      priceLink.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        this.downloadPrice();
      });
    }
  }

  /**
   * Обработка кликов по элементам навигации
   * @param {Event} event - Событие клика
   * @param {Element} item - Элемент навигации
   */
  handleNavigationClick(event, item) {
    // Проверяем, если это ссылка "Прайс", то не обрабатываем как обычную навигацию
    if (item.id === 'priceLink') {
      return;
    }

    // Удаляем активный класс у всех элементов
    const navItems = this.navigationBar?.querySelectorAll('.nav-item');
    navItems?.forEach(navItem => {
      navItem.classList.remove(CSS_CLASSES.ACTIVE);
      navItem.setAttribute('tabindex', '-1');
    });

    // Добавляем активный класс к текущему элементу
    item.classList.add(CSS_CLASSES.ACTIVE);
    item.setAttribute('tabindex', '0');

    // Эмитируем событие навигации
    this.emit('navigationItemClick', {
      item,
      text: item.textContent.trim(),
      href: item.getAttribute('href'),
    });
  }

  /**
   * Обработка скролла страницы
   */
  handleScroll() {
    const currentScrollPosition = getScrollPosition();

    // Проверяем, нужно ли делать навигацию sticky
    this.updateStickyState(currentScrollPosition);

    // Обновляем видимость sticky логотипа
    this.updateStickyLogo(currentScrollPosition);

    // Эмитируем событие скролла
    this.emit('navigationScroll', {
      position: currentScrollPosition,
      isSticky: this.isSticky,
      headerHeight: this.headerHeight,
    });
  }

  /**
   * Обновление состояния sticky навигации
   * @param {number} scrollPosition - Текущая позиция скролла
   */
  updateStickyState(scrollPosition) {
    const shouldBeSticky = scrollPosition >= this.navbarOffsetTop;

    if (shouldBeSticky && !this.isSticky) {
      // Делаем навигацию sticky
      this.makeSticky();
    } else if (!shouldBeSticky && this.isSticky) {
      // Убираем sticky состояние
      this.removeSticky();
    }
  }

  /**
   * Включение sticky режима
   */
  makeSticky() {
    if (!this.navigationBar) return;

    this.navigationBar.classList.add(CSS_CLASSES.STICKY);
    this.isSticky = true;

    // Добавляем отступ к body, чтобы контент не прыгал
    document.body.style.paddingTop = `${this.navigationBar.offsetHeight}px`;

    // Эмитируем событие
    this.emit(EVENTS.NAVIGATION_STICKY);

    console.log('📌 Навигация стала sticky');
  }

  /**
   * Отключение sticky режима
   */
  removeSticky() {
    if (!this.navigationBar) return;

    this.navigationBar.classList.remove(CSS_CLASSES.STICKY);
    this.isSticky = false;

    // Убираем отступ у body
    document.body.style.paddingTop = '';

    // Эмитируем событие
    this.emit(EVENTS.NAVIGATION_NORMAL);

    console.log('📌 Навигация вернулась в обычный режим');
  }

  /**
   * Обновление видимости sticky логотипа
   * @param {number} scrollPosition - Текущая позиция скролла
   */
  updateStickyLogo(scrollPosition) {
    if (!this.stickyLogo) return;

    // Показываем логотип только когда header полностью скрыт
    const shouldShowLogo = this.isSticky && scrollPosition >= this.headerHeight;

    if (shouldShowLogo) {
      this.stickyLogo.style.display = 'flex';
    } else {
      this.stickyLogo.style.display = 'none';
    }
  }

  /**
   * Обработка изменения размера окна
   */
  handleResize() {
    // Пересчитываем позиции после изменения размера
    this.calculateInitialPositions();

    // Эмитируем событие
    this.emit('navigationResize', {
      navbarOffsetTop: this.navbarOffsetTop,
      headerHeight: this.headerHeight,
    });
  }

  /**
   * Плавная прокрутка к началу страницы
   * @param {number} duration - Продолжительность анимации
   */
  scrollToTop(duration = NAVIGATION_CONFIG.SMOOTH_SCROLL_DURATION) {
    const start = getScrollPosition();
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing функция для плавности
      const easeInOutCubic = progress => {
        return progress < 0.5
          ? 4 * progress * progress * progress
          : (progress - 1) * (2 * progress - 2) * (2 * progress - 2) + 1;
      };

      const currentPosition = start * (1 - easeInOutCubic(progress));
      window.scrollTo(0, currentPosition);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  /**
   * Плавная прокрутка к элементу
   * @param {string|Element} target - Селектор или элемент
   * @param {number} offset - Смещение в пикселях
   * @param {number} duration - Продолжительность анимации
   */
  scrollToElement(target, offset = NAVIGATION_CONFIG.SCROLL_OFFSET, duration = NAVIGATION_CONFIG.SMOOTH_SCROLL_DURATION) {
    const element = typeof target === 'string' ? querySelector(target) : target;

    if (!element) {
      console.warn(`Элемент для прокрутки не найден: ${target}`);
      return;
    }

    const elementTop = element.offsetTop - offset;
    const start = getScrollPosition();
    const distance = elementTop - start;
    const startTime = performance.now();

    const animateScroll = (currentTime) => {
      const timeElapsed = currentTime - startTime;
      const progress = Math.min(timeElapsed / duration, 1);

      // Easing функция
      const easeInOutCubic = progress => {
        return progress < 0.5
          ? 4 * progress * progress * progress
          : (progress - 1) * (2 * progress - 2) * (2 * progress - 2) + 1;
      };

      const currentPosition = start + distance * easeInOutCubic(progress);
      window.scrollTo(0, currentPosition);

      if (progress < 1) {
        requestAnimationFrame(animateScroll);
      }
    };

    requestAnimationFrame(animateScroll);
  }

  /**
   * Установка активного элемента навигации
   * @param {string} itemText - Текст элемента навигации
   */
  setActiveNavigationItem(itemText) {
    const navItems = this.navigationBar?.querySelectorAll('.nav-item');

    navItems?.forEach(item => {
      if (item.textContent.trim() === itemText) {
        item.classList.add(CSS_CLASSES.ACTIVE);
        item.setAttribute('tabindex', '0');
      } else {
        item.classList.remove(CSS_CLASSES.ACTIVE);
        item.setAttribute('tabindex', '-1');
      }
    });
  }

  /**
   * Получение текущего активного элемента навигации
   * @returns {Element|null} - Активный элемент навигации
   */
  getActiveNavigationItem() {
    return this.navigationBar?.querySelector(`.nav-item.${CSS_CLASSES.ACTIVE}`) || null;
  }

  /**
   * Проверка, находится ли навигация в sticky режиме
   * @returns {boolean} - Состояние sticky режима
   */
  isStickyMode() {
    return this.isSticky;
  }

  /**
   * Принудительное обновление позиций навигации
   */
  updatePositions() {
    this.calculateInitialPositions();

    // Пересчитываем sticky состояние
    const currentScrollPosition = getScrollPosition();
    this.updateStickyState(currentScrollPosition);
    this.updateStickyLogo(currentScrollPosition);
  }

  /**
   * Скрытие навигации (например, при полноэкранном режиме)
   */
  hideNavigation() {
    if (this.navigationBar) {
      this.navigationBar.style.transform = 'translateY(-100%)';
    }
  }

  /**
   * Показ навигации
   */
  showNavigation() {
    if (this.navigationBar) {
      this.navigationBar.style.transform = '';
    }
  }

  /**
   * Скачивание прайс-листа
   */
  downloadPrice() {
    const link = document.createElement('a');
    link.href = '/data/price.xlsx';
    link.download = 'Прайс-лист Видиостудии КВАРТ.xlsx';
    link.style.display = 'none';
    
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    
    console.log('💰 Скачивание прайс-листа');
  }

  /**
   * Получение статистики навигации
   * @returns {Object} - Статистика
   */
  getStatistics() {
    return {
      isSticky: this.isSticky,
      navbarOffsetTop: this.navbarOffsetTop,
      headerHeight: this.headerHeight,
      currentScrollPosition: getScrollPosition(),
      activeItem: this.getActiveNavigationItem()?.textContent.trim() || null,
    };
  }

  /**
   * Деструктор
   */
  destroy() {
    // Убираем sticky режим
    if (this.isSticky) {
      this.removeSticky();
    }

    // Убираем обработчики событий
    if (this.throttledScrollHandler) {
      window.removeEventListener('scroll', this.throttledScrollHandler);
    }

    this.removeAllListeners();

    this.navigationBar = null;
    this.stickyLogo = null;
    this.header = null;
  }
}
